import "jspdf-autotable";
import { pdfHeader } from "@/utilities/pdfUtils";

/**
 * Generate PDF for attendance history
 * @param {Object} pdfData - Data prepared for PDF generation
 * @param {Object} settings - Application settings (for logo)
 * @param {Function} gtag - Google Analytics tracking function
 * @param {String} locale - Locale code (e.g., 'fr-FR', 'en-US')
 * @param {Function} t - Translation function (i18n.t)
 * @returns {Promise<Object>} - Promise that resolves with object containing doc and filename
 */
export async function generateHistoryPdf(pdfData, settings, gtag, locale, t) {

  // Track event
  if (gtag) {
    gtag.event("Imp historique assiduités", {
      event_category: "Impression PDF",
      event_label: "Historique assiduités",
      value: 1,
    });
  }

  const title = pdfData.className || t("attendanceHistoryPdf.classe");
  const periodText = `${t("attendanceHistoryPdf.period")} : ${pdfData.period}`;
  const doc = pdfHeader(settings, {
    title: title,
    subTitle: periodText,
    portrait: false,
    locale: locale,
  });
  const pdf_width = doc.internal.pageSize.width;
  const pdf_height = doc.internal.pageSize.height;
  const headerBottomY = 45;
  let yPos = headerBottomY;

  // Constants
  const SESSIONS_PER_PAGE = 11;
  const leftMargin = 10;
  const rightMargin = 10;
  const availableWidth = pdf_width - leftMargin - rightMargin;

  // Fixed widths for specific columns
  const fixedWidths = {
    num: 15,
    name: 30,
  };

  // Calculate width for session columns - always use fixed width for 11 columns
  const totalFixedWidth = fixedWidths.num + fixedWidths.name;
  const remainingWidth = availableWidth - totalFixedWidth;
  // Always calculate column width for 11 columns to ensure consistency
  const sessionColumnWidth = Math.max(15, remainingWidth / SESSIONS_PER_PAGE);

  // Color mapping for attendance status
  const getAttendanceColor = (symbol) => {
    switch (symbol) {
      case "P": // Présent - Green
        return [103, 194, 58];
      case "A": // Absence - Red
        return [245, 108, 108];
      case "R": // Retard - Yellow
        return [255, 193, 7];
      case "E": // Exclus - Orange
        return [255, 152, 0];
      default:
        return null; // No color for other values
    }
  };

  // Helper function to get attendance symbol
  const getAttendanceSymbol = (attendanceType) => {
    // Map translated attendance types to symbols
    const presentText = t("attendanceHistoryPdf.present");
    const absenceText = t("attendanceHistoryPdf.absence");
    const retardText = t("attendanceHistoryPdf.retard");
    const exclusText = t("attendanceHistoryPdf.exclus");
    
    switch (attendanceType) {
      case "Présent":
      case presentText:
        return "P";
      case "Absence":
      case absenceText:
        return "A";
      case "Retard":
      case retardText:
        return "R";
      case "Exclus":
      case exclusText:
        return "E";
      default:
        return attendanceType ? attendanceType.charAt(0) : "P";
    }
  };

  // Split sessions into chunks of 11
  const totalSessions = pdfData.sessions.length;
  const sessionChunks = [];
  for (let i = 0; i < totalSessions; i += SESSIONS_PER_PAGE) {
    sessionChunks.push(pdfData.sessions.slice(i, i + SESSIONS_PER_PAGE));
  }

  // Generate table for each chunk
  sessionChunks.forEach((sessionChunk, chunkIndex) => {
    // Add new page if not the first chunk
    if (chunkIndex > 0) {
      doc.addPage();
      pdfHeader(settings, {
        title: title,
        subTitle: periodText,
        portrait: false,
        existingDoc: doc,
      });
      yPos = headerBottomY;
    }
    const startingPageNumber = doc.internal.getNumberOfPages();
    const numSessionsInChunk = sessionChunk.length;
    const headers = [
      t("attendanceHistoryPdf.num"),
      t("attendanceHistoryPdf.studentName")
    ];
    sessionChunk.forEach((session) => {
      const headerText = `${session.formattedDate}\n${session.subjectName}`;
      headers.push(headerText);
    });

    const numEmptyColumns = SESSIONS_PER_PAGE - numSessionsInChunk;
    for (let i = 0; i < numEmptyColumns; i++) {
      headers.push("");
    }

    // Prepare table data for this chunk
    const tableData = [];
    let counter = 1;

    pdfData.students.forEach((student) => {
      const row = [counter, student.fullName];

      // Get attendance for sessions in this chunk
      sessionChunk.forEach((session) => {
        const attendance = student.attendanceBySession.find(
          (att) =>
            String(att.sessionId) === String(session.scheduleId) &&
            att.date === session.date
        );
        const defaultPresent = t("attendanceHistoryPdf.present");
        const symbol = getAttendanceSymbol(
          attendance ? attendance.type : defaultPresent
        );
        row.push(symbol);
      });

      // Add empty cells if fewer than 11 sessions
      for (let i = 0; i < numEmptyColumns; i++) {
        row.push("");
      }

      tableData.push(row);
      counter++;
    });

    // Build columnStyles for this chunk
    const columnStyles = {
      0: { cellWidth: fixedWidths.num, halign: "center" }, // Num
      1: { cellWidth: fixedWidths.name }, // Nom
    };

    // Add session column styles (including empty columns)
    for (let i = 2; i < 2 + SESSIONS_PER_PAGE; i++) {
      columnStyles[i] = {
        cellWidth: sessionColumnWidth,
        halign: "center",
        fontSize: 7,
      };
    }

    // Generate table for this chunk
    doc.autoTable({
      theme: "grid",
      styles: {
        font: "Amiri",
        fontSize: 7,
        cellPadding: 1.5,
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 6,
        cellPadding: 2,
      },
      margin: {
        top: yPos + 5,
        left: leftMargin,
        right: rightMargin,
        bottom: 30,
      },
      tableWidth: "auto", // Use auto to fill available width
      head: [headers],
      body: tableData,
      columnStyles: columnStyles,
      willDrawCell: function (data) {
        // Apply background colors to attendance status cells
        // Only color cells that have actual session data (not empty columns)
        if (
          data.row.index >= 0 &&
          data.column.index >= 2 &&
          data.column.index < 2 + numSessionsInChunk
        ) {
          const cellValue =
            data.cell.text && data.cell.text[0] ? data.cell.text[0] : "";
          const color = getAttendanceColor(cellValue);

          if (color) {
            doc.setFillColor(color[0], color[1], color[2]);
            doc.rect(
              data.cell.x,
              data.cell.y,
              data.cell.width,
              data.cell.height,
              "F"
            );
            doc.setTextColor(0, 0, 0);
          }
        }
      },
      didDrawCell: function (data) {
        doc.setTextColor(0, 0, 0);
      },
      didDrawPage: function (data) {
        const absolutePageNumber = startingPageNumber + data.pageNumber - 1;

        // Draw page number
        doc.setFontSize(8);
        const pageText = `${t("attendanceHistoryPdf.page")} ${absolutePageNumber}`;
        doc.text(pageText, pdf_width / 2, pdf_height - 10, {
          align: "center",
        });

        const tableBottomY = data.cursor ? data.cursor.y : pdf_height - 40;
        const legendMargin = 8;
        const maxLegendY = pdf_height - 25;
        const legendStartY = Math.min(tableBottomY + legendMargin, maxLegendY);
        const legendX = leftMargin;
        const squareSize = 4;
        const spacing = 50;

        doc.setFontSize(8);
        const legendLabel = `${t("attendanceHistoryPdf.legend")}:`;
        doc.text(legendX, legendStartY, legendLabel);

        const legendY = legendStartY + 5;
        const presentText = t("attendanceHistoryPdf.present");
        const absenceText = t("attendanceHistoryPdf.absence");
        const retardText = t("attendanceHistoryPdf.retard");
        const exclusText = t("attendanceHistoryPdf.exclus");

        // Présent - Green
        doc.setFillColor(103, 194, 58);
        doc.rect(legendX, legendY, squareSize, squareSize, "F");
        doc.text(legendX + 6, legendY + 3, `P = ${presentText}`);

        // Absence - Red
        doc.setFillColor(245, 108, 108);
        doc.rect(legendX + spacing, legendY, squareSize, squareSize, "F");
        doc.text(legendX + spacing + 6, legendY + 3, `A = ${absenceText}`);

        // Retard - Yellow
        doc.setFillColor(255, 193, 7);
        doc.rect(legendX + spacing * 2, legendY, squareSize, squareSize, "F");
        doc.text(legendX + spacing * 2 + 6, legendY + 3, `R = ${retardText}`);

        // Exclus - Orange
        doc.setFillColor(255, 152, 0);
        doc.rect(legendX + spacing * 3, legendY, squareSize, squareSize, "F");
        doc.text(legendX + spacing * 3 + 6, legendY + 3, `E = ${exclusText}`);
      },
    });
  });

  // Generate filename
  const historyText = t("attendanceHistoryPdf.history");
  const filename = `${pdfData.className}_${historyText}_${pdfData.startDate.replace(
    /\//g,
    "-"
  )}_${pdfData.endDate.replace(/\//g, "-")}`;

  return { doc, filename };
}

